import { inject, Injectable, signal } from "@angular/core";
import { CustomFormArray, CustomFormControl, CustomFormGroup } from "@tilby/tilby-ui-lib/components/tilby-magic-form";
import { ItemDataCatalogService } from "src/app/features/items/services/item-data-catalog.service";
import { ItemChannelFields, ItemChannelsForm, ItemChannelsFormGroups } from "./item-channels.model";
import { ItemDefault, ItemFields } from "src/app/features/items/models/items-form.model";
import { Items, ItemsChannels } from "tilby-models";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class ItemChannelsFormService {
    
    private readonly itemDataCatalogService = inject(ItemDataCatalogService);
    private readonly translate = inject(TranslateService);

    private itemChannelsForm!: CustomFormGroup<ItemChannelsFormGroups>;    
    itemChannelsFormReady = signal(false);

    get form() {
        return this.itemChannelsForm;
    }

    channelsFormArray: CustomFormGroup<ItemChannelsForm>[] = [];

    createForm(item: ItemFields = new ItemDefault() ,hiddenFields?:Array<keyof ItemFields>) {
        if(this.channelsFormArray?.length == 0) {
            const channelsData: Partial<ItemChannelFields>[]  = [...this.itemDataCatalogService.getDataByKey("channels") ?? []]
                .filter(ch => ch.id !== 'pos') as ItemChannelFields[];
    
            channelsData.unshift({
                name: this.translate.instant('ITEMS.DETAILS.CASHREGISTER'),
                category_id: item.category_id,
                _enabled: true,
                _imgUrl: 'assets/images/channels/pos.png',
                _readOnly: true
            });
    
            const itemChannelsMap = item.channels?.reduce((acc, channel) => {
                acc[channel.channel_id] = channel;
                return acc;
            }, {} as { [key: string]: ItemsChannels });
    
            channelsData.forEach((channel, index) => {
                if(index == 0) {
                    this.channelsFormArray.push(new CustomFormGroup<ItemChannelsForm>({
                        _readOnly: new CustomFormControl(
                            true
                        ),
                        _enabled: new CustomFormControl(
                            {value: channelsData[0]._enabled, disabled: true},
                        ),
                        name: new CustomFormControl(
                            channelsData[0].name || null
                        ),
                        default_pricelist: new CustomFormControl(
                            null
                        )
                    }));
                } else {
                    const associatedItemChannel = itemChannelsMap && channel?.id? itemChannelsMap[channel.id]: null;
                    Object.assign(channel, {
                        _imgUrl: channel.image_url || `assets/images/channels/${channel.id}.png`,
                        _enabled: !!associatedItemChannel,
                        _entId: associatedItemChannel?.id,
                        category_id: associatedItemChannel?.category_id ?? null,
                        default_pricelist: associatedItemChannel?.default_pricelist ?? 1
                    });
        
                    this.channelsFormArray.push(new CustomFormGroup<ItemChannelsForm>({
                        _enabled: new CustomFormControl(
                            !!associatedItemChannel
                        ),
                        _entId: new CustomFormControl(
                            channel._entId || null
                        ),
                        channel_id: new CustomFormControl(
                            channel.id || null
                        ),
                        name: new CustomFormControl(
                            channel.name || null
                        ),
                        _imgUrl: new CustomFormControl(
                            channel._imgUrl || null
                        ),
                        default_pricelist: new CustomFormControl(
                            {value: channel.default_pricelist || null, disabled: !!!associatedItemChannel}
                        )
                    }))
                }
            })
        }

        this.itemChannelsForm = new CustomFormGroup({
            channels: new CustomFormArray<CustomFormGroup<ItemChannelsForm>>(
                this.channelsFormArray
            )
        })

        this.itemChannelsFormReady.set(true);
        return this.itemChannelsForm;
    }

    resetFormValues(item: Items = new ItemDefault()) {
        const itemChannelsMap = item.channels?.reduce((acc, channel) => {
            acc[channel.channel_id] = channel;
            return acc;
        }, {} as { [key: string]: ItemsChannels }) || {};

        this.channelsArray.controls.forEach((control, index) => {
            const group = control as CustomFormGroup<ItemChannelsForm>;

            if (index > 0) {
                // Recupero l'ID del canale dal controllo stesso 
                const currentChannelId = group.get('channel_id')?.value;
                
                // Cerchiamo se questo canale esiste nel nuovo oggetto 'item'
                const associatedItemChannel = currentChannelId ? itemChannelsMap[currentChannelId] : null;

                // Logica di abilitazione/disabilitazione del campo 'default_pricelist'
                const priceListControl = group.get('default_pricelist');
                
                if (associatedItemChannel) {
                    // Il canale è attivo nell'item
                    // Abilito il controllo prezzi
                    priceListControl?.enable({ emitEvent: false });

                    // Aggiorno i valori
                    group.patchValue({
                        _enabled: true,
                        _entId: associatedItemChannel.id,
                        default_pricelist: associatedItemChannel.default_pricelist
                    }, { emitEvent: false });

                } else {
                    // Disabilito il controllo prezzi
                    priceListControl?.disable({ emitEvent: false });

                    // Resetto i valori
                    group.patchValue({
                        _enabled: false,
                        _entId: null,
                        default_pricelist: 1
                    }, { emitEvent: false });
                }
            }
        });

        this.itemChannelsForm.updateValueAndValidity();
        this.itemChannelsForm.markAsPristine();
    }

    getChannelsFields(currentItem: Items) {
        // Recupero tutte le chiavi (nomi dei campi) presenti nel form
        const channelIndexToMap = Object.keys(this.channelsArray.controls);
        
        // Clono l'oggetto di destinazione per non mutare l'originale
        const updatedItem = { ...currentItem }; 

        // Creo lista di canali da aggiungere al prodotto
        let channelsList: ItemsChannels[] = [];

        // Ciclo su tutte le chiavi trovate nel form
        channelIndexToMap.forEach(index => {
            const idNumber = Number(index);
            if(idNumber !== 0 && this.getEnabled(idNumber)?.value) {
                channelsList.push({
                    channel_id: this.getChannelId(idNumber)?.value,
                    default_pricelist: this.getDefaultPriceList(idNumber)?.value,
                    ...(this.getEntId(idNumber)?.value && {id: this.getEntId(idNumber)?.value})
                });
            }
        });

        updatedItem['channels'] = channelsList;
        return updatedItem;
    }

    get channelsArray() {
        return this.itemChannelsForm?.controls.channels;
    }

    getEnabled(index: number) {
        return this.channelsArray?.at(index).controls._enabled;
    }

    getReadOnly(index: number) {
        return this.channelsArray?.at(index).controls._readOnly;
    }

    getImgUrl(index: number) {
        return this.channelsArray?.at(index).controls._imgUrl;
    }

    getEntId(index: number) {
        return this.channelsArray?.at(index).controls._entId;
    }

    getChannelId(index: number) {
        return this.channelsArray?.at(index).controls.channel_id;
    }

    getName(index: number) {
        return this.channelsArray?.at(index).controls.name;
    }

    getDefaultPriceList(index: number) {
        return this.channelsArray?.at(index).controls.default_pricelist;
    }

}